import { Control, FieldPath, FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@/shared/components/ui/form';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select';
import {
  getKpiGroupTranslation,
  getKpiUnitTranslation,
} from '@/shared/hooks/use-kpi-unit-translation';
import { KPI_GROUPS, KPI_UNITS_BY_GROUP } from '@/shared/types/kpi';

interface KpiUnitFieldProps<T extends FieldValues> {
  name: FieldPath<T>;
  label: string;
  placeholder: string;
  control: Control<T>;
  disabled?: boolean;
  isRelative?: boolean;
}

export const KpiUnitField = <T extends FieldValues>({
  name,
  label,
  placeholder,
  control,
  disabled = false,
  isRelative = false,
}: KpiUnitFieldProps<T>) => {
  const { t: tKpiTypes } = useTranslation('kpiTypes', { nsMode: 'fallback' });

  const filteredGroups = isRelative
    ? Object.entries(KPI_UNITS_BY_GROUP).filter(
        ([group]) => group === KPI_GROUPS.RELATIVE,
      )
    : Object.entries(KPI_UNITS_BY_GROUP).filter(
        ([group]) =>
          group !== KPI_GROUPS.OTHER && group !== KPI_GROUPS.RELATIVE,
      );

  return (
    <FormField
      name={name}
      control={control}
      render={({ field }) => (
        <FormItem>
          <Select onValueChange={field.onChange} defaultValue={field.value}>
            <FormControl>
              <SelectTrigger isInputTrigger disabled={disabled || isRelative}>
                <SelectValue
                  asLabelInput
                  hideDropdownIcon={disabled || isRelative}
                  label={label}
                  placeholder={placeholder}
                >
                  {getKpiUnitTranslation(tKpiTypes, field.value)}
                </SelectValue>
              </SelectTrigger>
            </FormControl>
            <SelectContent>
              {filteredGroups.map(([group, units]) => (
                <SelectGroup key={group}>
                  <SelectLabel>
                    {getKpiGroupTranslation(tKpiTypes, group)}
                  </SelectLabel>
                  {units.map((unit) => (
                    <SelectItem key={unit} value={unit}>
                      {getKpiUnitTranslation(tKpiTypes, unit)}
                    </SelectItem>
                  ))}
                </SelectGroup>
              ))}
            </SelectContent>
          </Select>
          <FormMessage />
        </FormItem>
      )}
    />
  );
};
